/**
 * useAttentionDone (KAN-750, KAN-753, KAN-852)
 *
 * Взять клиента в работу из блока внимания (бывшее "Отработано"): строка
 * уходит из внимания, бот больше не пишет первым, клиент закрепляется за
 * менеджером. Какие действия есть, решает workActions по роли:
 *   - take: "Взять в работу" себе (обычный менеджер везде, старший в приложении);
 *     запрос сразу, тост "Взято в работу: бот больше не пишет первым";
 *   - openAssign: "Назначить в работу" (владелец, оператор, старший): окно
 *     выбора менеджера (AttentionDoneDialog), по умолчанию текущий менеджер
 *     клиента, а у ничьего клиента менеджер сессии кабинета (KAN-753), если выбран;
 *   - request: основное действие поверхности (take или openAssign), его подпись в label.
 * Обычному менеджеру backend manager_id не читает и закрепляет ничьего за ним.
 */
import { useState } from 'react'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useSessionManager } from '@/hooks/useSessionManager'
import {
  ASSIGNED_TO_WORK_TEXT,
  primaryWorkLabel,
  TAKEN_TO_WORK_TEXT,
  workActions,
  type WorkSurface,
} from '@/lib/workActions'
import type { AttentionItem } from '@/services/api/dashboard'
import type { ManagerBrief } from '@/types/manager'
import type { AttentionAction } from './useAttentionActions'

export type RunAttentionAction = (
  clientId: string,
  action: AttentionAction,
  managerId?: string | null,
  successMessage?: string
) => void

export function useAttentionDone(run: RunAttentionAction, surface: WorkSurface = 'cabinet') {
  const { data: user } = useCurrentUser()
  const { manager: sessionManager, locked } = useSessionManager()
  const work = workActions(user, surface)
  // Строка, для которой открыто окно "Назначить в работу"; null: окно закрыто.
  const [dialogItem, setDialogItem] = useState<AttentionItem | null>(null)

  const take = (item: Pick<AttentionItem, 'client_id'>) =>
    run(item.client_id, 'done', work.selfId ?? undefined, TAKEN_TO_WORK_TEXT)
  const openAssign = (item: AttentionItem) => setDialogItem(item)

  const confirm = (managerId: string | null) => {
    if (dialogItem) run(dialogItem.client_id, 'done', managerId, ASSIGNED_TO_WORK_TEXT)
    setDialogItem(null)
  }

  // Менеджер сессии задан входом (менеджер): он и так берёт себе, в окне не подставляется.
  const defaultManager: ManagerBrief | null = locked ? null : sessionManager

  return {
    work,
    label: primaryWorkLabel(work),
    dialogItem,
    defaultManager,
    take,
    openAssign,
    request: (item: AttentionItem) => (work.takeSelf ? take(item) : openAssign(item)),
    confirm,
    cancel: () => setDialogItem(null),
  }
}
